import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MdOutlineColorLens, MdPhoto } from 'react-icons/md'
import { faceSwap, scenceSwapIcon } from 'shared-ui/assets'
import { LazyImage } from 'shared-ui/components'
import { useTheme } from 'styled-components'
import { useResponsive, useRemixTools } from 'shared-ui/hooks'
import { media } from 'shared-ui/utils/media'
import { useDispatch } from 'react-redux'
import {
  COMMON_MESSAGES,
  AI_TOOL_IDS,
  ROUTES
} from 'shared-ui/utils/constants'
import {
  updateGenerationSession,
  resetGenerationSession
} from 'shared-ui/redux/reducers/generationSessionReducer'
import { resetUploadedFile } from 'shared-ui/redux/reducers/uploadFileReducer'

const RemixBottomSheet = ({
  open,
  onClose,
  contentType = '',
  isLoading,
  isUGC,
  contentUrl
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useResponsive(media.mobile)
  const [isClosing, setIsClosing] = useState(false)
  const { data } = useRemixTools(contentType)
  const dispatch = useDispatch()

  const isUgcContent = Boolean(isUGC === true || isUGC === 'true')
  const shouldRemix = !isUgcContent && Boolean(contentUrl)

  useEffect(() => {
    if (!open) {
      setIsClosing(false)
    }
  }, [open])

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose && onClose()
    }, 300)
  }

  const getToolIcon = (name) => {
    switch (name) {
      case 'Face Swap':
        return <LazyImage src={faceSwap} alt="faceIcon" />
      case 'Colorise':
        return <MdOutlineColorLens className="text-white text-2xl" />
      case 'Scene Swap':
        return <LazyImage src={scenceSwapIcon} alt="Scence Swap" />
      default:
        return <MdPhoto size={40} color="#fff" />
    }
  }

  const handleTool = (item) => {
    onClose && onClose()

    if (shouldRemix) {
      dispatch(
        updateGenerationSession({
          thumbnail_url: contentUrl,
          file_url: contentUrl,
          isRemixFlag: true,
          content_type: contentType,
          ai_tool_id: item?.id
        })
      )
    } else {
      dispatch(resetGenerationSession())
      dispatch(resetUploadedFile())
    }

    const navState = shouldRemix ? { state: { isRemix: true } } : undefined

    if (item?.id === AI_TOOL_IDS.VOICE_REMIX) {
      navigate(ROUTES.voiceRemix)
    } else if (item?.id === AI_TOOL_IDS.VOICE_CLONE) {
      navigate(ROUTES.voiceCloneLanding)
    } else if (item?.id === AI_TOOL_IDS.IMAGE_TO_VIDEO) {
      navigate(ROUTES.imageToVideo)
    } else if (item?.id === AI_TOOL_IDS.TALKING_AVATAR) {
      navigate(ROUTES.talkingAvatarLanding)
    } else if (item?.id === AI_TOOL_IDS.TEXT_TO_VIDEO) {
      navigate(ROUTES.textToVideo)
    } else if (item?.id === AI_TOOL_IDS.ACTION_GENERATION) {
      navigate(ROUTES.actionGeneration)
    } else if (item?.id === AI_TOOL_IDS.TEXT_TO_IMAGE) {
      navigate(ROUTES.textToImage)
    } else if (item.id === AI_TOOL_IDS.MOTION_CONTROL) {
      navigate(ROUTES.motionControl)
    } else if (item.id === AI_TOOL_IDS.FACE_SWAP) {
      navigate(ROUTES.faceSwap, navState)
    } else if (item.id === AI_TOOL_IDS.COLORIZE) {
      navigate(ROUTES.colorize, navState)
    } else if (item.id === AI_TOOL_IDS.SCENE_SWAP) {
      navigate(ROUTES.sceneSwap, navState)
    }
  }
  if (!open) return null

  return (
    <div
      className={`fixed inset-0 z-50 flex transition-opacity duration-300 bg-black ${
        isClosing ? 'bg-opacity-0' : 'bg-opacity-50'
      } ${isMobile ? 'items-end justify-center' : 'items-center justify-end'}`}
      onClick={handleClose}
    >
      <div
        className={`${
          isMobile
            ? 'w-[96vw] h-[60vh] max-w-md mx-auto rounded-t-2xl pt-4 pb-8'
            : 'w-[400px] h-full pt-4 pb-8'
        } relative flex flex-col border border-${theme.app.iconlabel} shadow-lg overflow-auto ${
          isClosing
            ? ''
            : isMobile
              ? 'animate-slideInUp'
              : 'animate-slideInRight'
        }`}
        style={{
          background: theme.app.background,
          transform: isClosing
            ? isMobile
              ? 'translateY(100%)'
              : 'translateX(100%)'
            : 'translate(0, 50px)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        {isMobile && (
          <div className="w-12 h-1 bg-gray-700 rounded-full mx-auto mb-4" />
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-6 mb-2">
          <h2
            className="text-2xl font-normal"
            style={{ color: theme.app.text }}
          >
            {data?.title}
          </h2>
          <button
            className="text-3xl"
            onClick={handleClose}
            style={{ color: theme.app.text }}
          >
            ×
          </button>
        </div>
        {/* Description */}
        <div
          className="px-6 mb-4 font-normal text-sm text-left"
          style={{ color: theme.app.text }}
        >
          {data?.subtitle}
        </div>
        {/* Tools Grid */}
        <div className="px-4 grid grid-cols-2 gap-2">
          {isLoading ? (
            <div
              className="col-span-2 text-center text-gray-400 py-8"
              style={{ color: theme.app.text }}
            >
              {COMMON_MESSAGES.Loading}
            </div>
          ) : data && data?.length === 0 ? (
            <div
              className="col-span-2 text-center text-gray-400 py-8"
              style={{ color: theme.app.text }}
            >
              {COMMON_MESSAGES.NoData}
            </div>
          ) : (
            data?.data[0]?.assets?.map((item) => {
              const toolName = item?.name || ''
              const toolCaption = item?.caption || ''

              return (
              <div
                key={item.id}
                className="relative w-full h-[120px] rounded-2xl border border-[#00B8F8] overflow-hidden cursor-pointer"
                style={{
                  background: theme.app.background,
                  borderColor: theme.app.accent
                }}
                onClick={() => {
                  handleTool(item)
                }}
              >
                {/* Icon in the top-left corner */}
                <div className="absolute top-3 left-3 z-10 h-6 w-6">
                  {getToolIcon(toolName)}
                </div>
                <LazyImage
                  src={item.thumbnail}
                  alt={toolName}
                  className="w-full h-full object-cover relative z-0"
                />
                <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-2 left-2 right-2 z-10 text-white">
                  <div className="font-semibold text-base text-left line-clamp-1 drop-shadow-sm">
                    {toolName}
                  </div>
                  <div className="text-xs text-left text-gray-300 drop-shadow-sm">
                    {toolCaption}
                  </div>
                </div>
              </div>
              )
            })
          )}
        </div>
        <style>{`
                    .animate-slideInUp { animation: slideInUp 0.3s cubic-bezier(0.4,0,0.2,1); }
                    .animate-slideInRight { animation: slideInRight 0.3s cubic-bezier(0.4,0,0.2,1); }
                    @keyframes slideInUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                    @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
                `}</style>
      </div>
    </div>
  )
}

export default RemixBottomSheet